import { useRef, useState, useMemo, useEffect } from "react";
import {
  Grid,
  Typography,
  Box,
  Button,
  IconButton,
  Popover,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SentimentSatisfiedAltOutlinedIcon from "@mui/icons-material/SentimentSatisfiedAltOutlined";
import { useTheme } from "@mui/material/styles";
import EmojiPicker from "emoji-picker-react";
import emojiDataPt from "emoji-picker-react/dist/data/emojis-pt";

import { allVariables, categories } from "./variables";
import { VariableDialog } from "./VariableDialog";
import {
  splitTextByVariableTokens,
  convertSuggestedVariableToToken,
} from "../../utils/variableTokens";

const CHANNEL_LIMITS = {
  whatsapp_api: 1000,
  whatsapp_web: 1000,
  sms: 160,
  email: 5000,
};

const getChannelLimit = (channel) => CHANNEL_LIMITS[channel] || 1000;

const getGraphemes = (text) => {
  const source = String(text || "");
  if (typeof Intl !== "undefined" && Intl.Segmenter) {
    const segmenter = new Intl.Segmenter("pt-BR", { granularity: "grapheme" });
    return Array.from(segmenter.segment(source), (s) => s.segment);
  }
  return Array.from(source);
};

const countChars = (text) => getGraphemes(text).length;
const clampToLimit = (text, limit) => getGraphemes(text).slice(0, limit).join("");
const INVALID_VARIABLE_QUERY_PATTERN = /[^A-Za-z0-9À-ÿ_]/;
const MIN_VARIABLE_AUTOCOMPLETE_QUERY = 3;
const CARET_OFFSET_Y = 8;

const getCaretAnchorPosition = (textarea, caretPosition) => {
  if (!textarea || typeof window === "undefined" || typeof document === "undefined") {
    return null;
  }

  const position = Number.isFinite(caretPosition) ? caretPosition : textarea.value.length;
  const computedStyle = window.getComputedStyle(textarea);
  const mirror = document.createElement("div");
  const span = document.createElement("span");
  const propertiesToCopy = [
    "boxSizing",
    "width",
    "height",
    "overflowX",
    "overflowY",
    "borderTopWidth",
    "borderRightWidth",
    "borderBottomWidth",
    "borderLeftWidth",
    "paddingTop",
    "paddingRight",
    "paddingBottom",
    "paddingLeft",
    "fontStyle",
    "fontVariant",
    "fontWeight",
    "fontStretch",
    "fontSize",
    "lineHeight",
    "fontFamily",
    "textAlign",
    "textTransform",
    "textIndent",
    "letterSpacing",
    "wordSpacing",
    "tabSize",
    "whiteSpace",
    "wordBreak",
    "overflowWrap",
  ];

  mirror.style.position = "fixed";
  mirror.style.left = "-9999px";
  mirror.style.top = "0";
  mirror.style.visibility = "hidden";
  mirror.style.whiteSpace = "pre-wrap";
  mirror.style.wordBreak = "break-word";
  mirror.style.overflowWrap = "break-word";

  propertiesToCopy.forEach((property) => {
    mirror.style[property] = computedStyle[property];
  });

  mirror.textContent = textarea.value.slice(0, position);
  span.textContent = textarea.value.slice(position) || ".";
  mirror.appendChild(span);
  document.body.appendChild(mirror);

  const textareaRect = textarea.getBoundingClientRect();
  const anchor = {
    top: textareaRect.top + span.offsetTop - textarea.scrollTop + CARET_OFFSET_Y,
    left: textareaRect.left + span.offsetLeft - textarea.scrollLeft,
  };

  document.body.removeChild(mirror);
  return anchor;
};

const getVariableTrigger = (text, cursorPosition) => {
  const content = String(text || "");
  const cursor = Number.isFinite(cursorPosition) ? cursorPosition : content.length;
  const beforeCursor = content.slice(0, cursor);
  const triggerStart = beforeCursor.lastIndexOf("{{");

  if (triggerStart < 0) return null;

  const tokenClose = content.indexOf("}}", triggerStart + 2);
  if (tokenClose !== -1 && tokenClose >= cursor) {
    return null;
  }

  const tokenChunk = beforeCursor.slice(triggerStart + 2);
  if (!tokenChunk && beforeCursor.endsWith("{{")) {
    return { start: triggerStart, end: cursor, query: "" };
  }

  if (tokenChunk.includes("}}")) return null;
  if (INVALID_VARIABLE_QUERY_PATTERN.test(tokenChunk)) return null;

  return { start: triggerStart, end: cursor, query: tokenChunk };
};

export default function TextSection({ error, messages, setMessages, channel }) {
  const theme = useTheme();
  const textareaRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("Tudo");
  const [emojiAnchorEl, setEmojiAnchorEl] = useState(null);
  const [editorScrollTop, setEditorScrollTop] = useState(0);
  const [editorScrollLeft, setEditorScrollLeft] = useState(0);
  const [editorViewport, setEditorViewport] = useState({ width: null, height: null });
  const [variableAutocomplete, setVariableAutocomplete] = useState(null);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(0);
  const [autocompleteAnchorPosition, setAutocompleteAnchorPosition] = useState(null);

  const autoReply = messages.find((m) => m.id === "auto-reply");
  const currentText = autoReply?.content || "";
  const limit = getChannelLimit(channel);
  const charCount = countChars(currentText);
  const isLimitReached = charCount >= limit;

  const filteredVariables = useMemo(() => {
    return allVariables.filter((v) => {
      const matchesSearch =
        v.value.toLowerCase().includes(searchTerm.toLowerCase()) ||
        v.category.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesTab =
        activeTab === "Tudo" || v.category === activeTab;

      return matchesSearch && matchesTab;
    });
  }, [searchTerm, activeTab]);

  const highlightedParts = useMemo(() => {
    return splitTextByVariableTokens(currentText);
  }, [currentText]);
  const suggestedRawVariables = useMemo(
    () =>
      Array.from(
        new Set(
          highlightedParts
            .filter((part) => part.state === "suggested")
            .map((part) => part.text),
        ),
      ),
    [highlightedParts],
  );

  const variableSuggestions = useMemo(() => {
    if (!variableAutocomplete) return [];
    const query = String(variableAutocomplete.query || "").toLowerCase();
    if (query.length < MIN_VARIABLE_AUTOCOMPLETE_QUERY) return [];
    const items = allVariables.filter((variable) =>
      variable.value.toLowerCase().includes(query),
    );
    return items.slice(0, 8);
  }, [variableAutocomplete]);

  const updateVariableAutocomplete = (text, cursor) => {
    const trigger = getVariableTrigger(text, cursor);
    setVariableAutocomplete(trigger);
    setActiveSuggestionIndex(0);

    if (!trigger) {
      setAutocompleteAnchorPosition(null);
      return;
    }

    const anchor = getCaretAnchorPosition(textareaRef.current, cursor);
    setAutocompleteAnchorPosition(anchor);
  };

  const insertVariable = (val) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const variableTag = `{{${val}}}`;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentContent = autoReply?.content || "";

    const newText =
      currentContent.substring(0, start) +
      variableTag +
      currentContent.substring(end);
    const limitedText = clampToLimit(newText, limit);

    setMessages((prev) =>
      prev.map((m) =>
        m.id === "auto-reply"
          ? { ...m, content: limitedText }
          : m,
      ),
    );

    setOpen(false);

    setTimeout(() => {
      textarea.focus();
      const nextCursor = Math.min(start + variableTag.length, countChars(limitedText));
      textarea.setSelectionRange(
        nextCursor,
        nextCursor,
      );
    }, 100);
  };

  const insertAutocompleteVariable = (val) => {
    const textarea = textareaRef.current;
    if (!textarea || !variableAutocomplete) return;

    const variableTag = `{{${val}}}`;
    const currentContent = autoReply?.content || "";
    const newText =
      currentContent.slice(0, variableAutocomplete.start) +
      variableTag +
      currentContent.slice(variableAutocomplete.end);
    const limitedText = clampToLimit(newText, limit);

    setMessages((prev) =>
      prev.map((m) =>
        m.id === "auto-reply"
          ? { ...m, content: limitedText }
          : m,
      ),
    );

    setVariableAutocomplete(null);

    setTimeout(() => {
      textarea.focus();
      const nextCursor = Math.min(variableAutocomplete.start + variableTag.length, limitedText.length);
      textarea.setSelectionRange(nextCursor, nextCursor);
    }, 0);
  };

  const insertEmoji = (emoji) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentContent = autoReply?.content || "";

    const newText =
      currentContent.substring(0, start) +
      emoji +
      currentContent.substring(end);
    const limitedText = clampToLimit(newText, limit);

    setMessages((prev) =>
      prev.map((m) =>
        m.id === "auto-reply"
          ? { ...m, content: limitedText }
          : m,
      ),
    );

    setEmojiAnchorEl(null);

    setTimeout(() => {
      textarea.focus();
      const nextCursor = Math.min(start + emoji.length, limitedText.length);
      textarea.setSelectionRange(nextCursor, nextCursor);
    }, 0);
  };

  const convertRawVariableToToken = (variableName) => {
    const convertedText = clampToLimit(
      convertSuggestedVariableToToken(currentText, variableName),
      limit,
    );

    setMessages((prev) =>
      prev.map((m) =>
        m.id === "auto-reply"
          ? { ...m, content: convertedText }
          : m,
      ),
    );

    setTimeout(() => {
      textareaRef.current?.focus();
    }, 0);
  };

  const syncEditorViewport = (textarea) => {
    if (!textarea) return;

    const nextWidth = textarea.clientWidth;
    const nextHeight = textarea.clientHeight;

    setEditorViewport((prev) => {
      if (prev.width === nextWidth && prev.height === nextHeight) return prev;
      return { width: nextWidth, height: nextHeight };
    });
  };

  useEffect(() => {
    syncEditorViewport(textareaRef.current);
  }, []);

  return (
    <>
      <Grid container spacing={2}>

        {/* HEADER */}
        <Grid
          item
          xs={12}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography sx={{ color: "#898989", fontSize: 16 }}>
            Mensagem de texto
          </Typography>

          <Button
            variant="text"
            startIcon={<AddIcon />}
            onClick={() => setOpen(true)}
            sx={{
              fontSize: 16,
              textTransform: "none",
              borderRadius: 2,
            }}
          >
            Adicionar Variável
          </Button>
        </Grid>

        {/* TEXTAREA */}
        <Grid item xs={12}>
          <Box
            sx={{
              width: "100%",
              minHeight: 250,
              p: 1.2,
              borderRadius: "12px",
              border: "1px solid",
              borderColor: error ? "error.main" : "grey.300",
              transition: "all 0.2s",
              backgroundColor: "#fcfcfc",
              "&:focus": {
                borderColor: theme.palette.primary.main,
                backgroundColor: "#fff",
                boxShadow: `0 0 0 3px ${theme.palette.primary.main}15`,
              },
            }}
          >
            <Box sx={{ position: "relative", overflow: "hidden", borderRadius: "10px" }}>
              <Box
                aria-hidden
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: editorViewport.width ? `${editorViewport.width}px` : "100%",
                  height: editorViewport.height ? `${editorViewport.height}px` : "100%",
                  boxSizing: "border-box",
                  p: 1,
                  borderRadius: "10px",
                  fontSize: "15px",
                  lineHeight: "1.6",
                  fontFamily: "inherit",
                  whiteSpace: "pre-wrap",
                  overflowWrap: "break-word",
                  wordBreak: "break-word",
                  overflow: "hidden",
                  pointerEvents: "none",
                  transform: `translate(${-editorScrollLeft}px, ${-editorScrollTop}px)`,
                }}
              >
                {highlightedParts.length > 0 ? (
                  highlightedParts.map((part) => (
                    <Box
                      key={part.key}
                      component="span"
                      sx={
                        part.state === "valid"
                          ? {
                            display: "inline",
                            backgroundColor: "#E9F1FF",
                            color: "#2F61BF",
                          }
                          : part.state === "invalid"
                            ? {
                              display: "inline",
                              backgroundColor: "#FFE9E5",
                              color: "#D1432F",
                            }
                            : part.state === "warning"
                              ? {
                                display: "inline",
                                backgroundColor: "#FFF3E8",
                                color: "#C77700",
                              }
                              : part.state === "suggested"
                                ? {
                                  display: "inline",
                                  backgroundColor: "#F1EDFF",
                                  color: "#6C52C8",
                                }
                          : { color: "transparent" }
                      }
                    >
                      {part.text}
                    </Box>
                  ))
                ) : (
                  <Box component="span" sx={{ color: "#98A2B3" }}>
                    Digite a mensagem que será enviada...
                  </Box>
                )}
              </Box>

              <Box
                ref={textareaRef}
                component="textarea"
                value={currentText}
                onKeyDown={(e) => {
                  if (!variableAutocomplete || !variableSuggestions.length) return;

                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setActiveSuggestionIndex((prev) => (prev + 1) % variableSuggestions.length);
                    return;
                  }

                  if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setActiveSuggestionIndex((prev) =>
                      prev <= 0 ? variableSuggestions.length - 1 : prev - 1,
                    );
                    return;
                  }

                  if (e.key === "Enter" || e.key === "Tab") {
                    e.preventDefault();
                    const selected = variableSuggestions[activeSuggestionIndex];
                    if (selected) insertAutocompleteVariable(selected.value);
                    return;
                  }

                  if (e.key === "Escape") {
                    setVariableAutocomplete(null);
                  }
                }}
                onClick={(e) => {
                  syncEditorViewport(e.target);
                  updateVariableAutocomplete(e.target.value, e.target.selectionStart);
                }}
                onScroll={(e) => {
                  setEditorScrollTop(e.target.scrollTop);
                  setEditorScrollLeft(e.target.scrollLeft);
                  syncEditorViewport(e.target);
                  updateVariableAutocomplete(e.target.value, e.target.selectionStart);
                }}
                onKeyUp={(e) => {
                  syncEditorViewport(e.target);
                  updateVariableAutocomplete(e.target.value, e.target.selectionStart);
                }}
                onChange={(e) => {
                  const limitedText = clampToLimit(e.target.value, limit);
                  setMessages((prev) =>
                    prev.map((m) =>
                      m.id === "auto-reply"
                        ? { ...m, content: limitedText }
                        : m,
                    ),
                  );
                  syncEditorViewport(e.target);
                  updateVariableAutocomplete(limitedText, e.target.selectionStart);
                }}
                sx={{
                  position: "relative",
                  zIndex: 1,
                  display: "block",
                  boxSizing: "border-box",
                  width: "100%",
                  minHeight: 190,
                  p: 1,
                  borderRadius: "10px",
                  fontSize: "15px",
                  lineHeight: "1.6",
                  fontFamily: "inherit",
                  border: "none",
                  outline: "none",
                  resize: "none",
                  overflowY: "auto",
                  overflowX: "hidden",
                  whiteSpace: "pre-wrap",
                  overflowWrap: "break-word",
                  wordBreak: "break-word",
                  backgroundColor: "transparent",
                  color: "#1F2937",
                  caretColor: "#1F2937",
                }}
              />
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 1 }}>
              <IconButton
                size="small"
                onClick={(e) => setEmojiAnchorEl(e.currentTarget)}
                sx={{ color: "#7a8699" }}
              >
                <SentimentSatisfiedAltOutlinedIcon />
              </IconButton>

              <Typography
                variant="caption"
                sx={{ color: isLimitReached ? "error.main" : "text.secondary", fontWeight: 600 }}
              >
                {charCount}/{limit}
              </Typography>
            </Box>

            {!!suggestedRawVariables.length && (
              <Box sx={{ px: 1, pt: 0.4, display: "flex", flexWrap: "wrap", gap: 0.8 }}>
                <Typography variant="caption" sx={{ color: "#6C52C8", alignSelf: "center" }}>
                  Possível variável sem {"{{}}"}:
                </Typography>
                {suggestedRawVariables.map((variableName) => (
                  <Button
                    key={variableName}
                    size="small"
                    variant="text"
                    onClick={() => convertRawVariableToToken(variableName)}
                    sx={{
                      textTransform: "none",
                      minWidth: "auto",
                      px: 1,
                      py: 0.2,
                      fontSize: 12,
                      color: "#6C52C8",
                    }}
                  >
                    Converter {variableName}
                  </Button>
                ))}
              </Box>
            )}
          </Box>

          {error && (
            <Typography color="error" variant="caption" sx={{ ml: 1 }}>
              Campo obrigatório
            </Typography>
          )}
        </Grid>
      </Grid>

      <Popover
        open={Boolean(emojiAnchorEl)}
        anchorEl={emojiAnchorEl}
        onClose={() => setEmojiAnchorEl(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 0.5 }}>
          <EmojiPicker
            onEmojiClick={(emojiData) => insertEmoji(emojiData.emoji)}
            emojiData={emojiDataPt}
            emojiStyle="native"
            searchDisabled={false}
            searchPlaceholder="Pesquisar"
            skinTonesDisabled
            previewConfig={{ showPreview: false }}
            width={286}
            height={332}
          />
        </Box>
      </Popover>

      <Popover
        open={Boolean(variableAutocomplete && variableSuggestions.length && autocompleteAnchorPosition)}
        anchorReference="anchorPosition"
        anchorPosition={autocompleteAnchorPosition || undefined}
        onClose={() => setVariableAutocomplete(null)}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box sx={{ width: 290, maxHeight: 260, overflowY: "auto", py: 0.5 }}>
          <List dense disablePadding>
            {variableSuggestions.map((variable, index) => (
              <ListItemButton
                key={variable.id}
                selected={index === activeSuggestionIndex}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => insertAutocompleteVariable(variable.value)}
                sx={{ px: 1.2, py: 0.5 }}
              >
                <ListItemText
                  primary={`{{${variable.value}}}`}
                  secondary={variable.category}
                  primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }}
                  secondaryTypographyProps={{ fontSize: 11 }}
                />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Popover>

      {/* DIALOG DE VARIÁVEIS */}
      <VariableDialog
        open={open}
        onClose={() => setOpen(false)}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        categories={categories}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        variables={filteredVariables}
        onSelect={insertVariable}
        theme={theme}
      />
    </>
  );
}
